// deployment_class: customer_env_allowed
/**
 * AuthProvider — bootstraps the auth store on mount by calling /v3/auth/me
 * and registers a periodic refresh. Plan 14 §Task 3 + OQ-Sh1.
 *
 * Session storage: httpOnly cookie set by Better Auth (`/api/auth/*`). The
 * provider does NOT read the cookie itself; /v3/auth/me uses it server-side.
 *
 * Session expiry handling (Plan 14 §Task 3 step 3): we don't have direct
 * access to the session token (httpOnly), so we re-validate via /v3/auth/me on a
 * configurable interval. A 401 from /v3/auth/me transitions the store to
 * 'unauthenticated' which causes RequireAuth/RequireScope to redirect to
 * the login page. The api-client (Task 8) layers a second guard: any 401
 * from a guarded endpoint also triggers logout + redirect.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './useAuth';

const REVALIDATE_INTERVAL_MS = 5 * 60_000;

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { refresh, status } = useAuth();

  // Initial bootstrap on mount.
  useEffect(() => {
    if (status === 'idle') {
      void refresh();
    }
  }, [refresh, status]);

  // Periodic re-validation while authenticated. SILENT: it must not flip status
  // to 'loading' (that unmounts the whole app via RequireAuthGate and resets the
  // dashboard to Home every interval). A real 401 still flips to 'unauthenticated'
  // → redirect to login; a transient network blip leaves the session intact.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const id = setInterval(() => {
      void refresh({ silent: true });
    }, REVALIDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status, refresh]);

  return <>{children}</>;
}
